import React, { Component } from "react";
import { cloneDeep as _cloneDeep, get as _get, isEmpty as _isEmpty, isEqual as _isEqual } from "lodash";
import { FormattedMessage, injectIntl } from "react-intl";
import { withCookies, Cookies } from "react-cookie";
import { Navigate, Link } from "react-router-dom";
import { instanceOf } from "prop-types";
import { Form } from "react-bootstrap";
import { connect } from "react-redux";
import Helmet from "react-helmet";

import { ErrorMessage } from "../../resusableComponents/common/formElements";
import { encryptUserData, decryptUserData } from "../../helpers/crypto";
import { withRouter } from "../../resusableComponents/hoc/withRouter";
import { isEmailValid, getBrowserOrigin } from "../../helpers/utils";
import config from "../../helpers/config";

import { doLogin, doMicrosoftLogin, clearAuthenticationError } from "../../redux/actions/authentication";

class LoginUser extends Component {

  static propTypes = {
    cookies: instanceOf(Cookies).isRequired
  };

  constructor(props) {
    super(props);

    const { cookies } = props;
    const tokenUserData = decryptUserData(cookies.get("loginData"));

    this.defaultFormData = { userName: _get(tokenUserData, "userName", ""), password: _get(tokenUserData, "password", ""), rememberMe: _get(tokenUserData, "rememberMe", false) };

    this.state = {
      formData: _cloneDeep(this.defaultFormData),
      formErrors: {},
      alertError: {},
      /** Preserved when login fails with "not registered" so we can deep-link to /request-access with Entra claims. */
      ssoRequestAccess: null,
      isLoginButtonClicked: false,
      disabled: true
    };
  }

  componentDidMount() {
    const { loginError, clearAuthenticationError, intl, doMicrosoftLogin } = this.props;

    if (!_isEmpty(loginError)) {
      const sso = _get(loginError, "ssoRequestAccess");
      this.setState({
        // If we will redirect to Request Access, do not flash the red SSO error on login.
        alertError: sso && sso.email ? {} : {
          title: _get(loginError, "title", "Alert"),
          message: _get(loginError, "message", "Something went wrong while login in."),
        },
        ssoRequestAccess: sso || null,
      });

      if (typeof clearAuthenticationError === "function") { clearAuthenticationError(); }
    }

    this._consumeMicrosoftSsoHash();

    const portalOrigin = this.getAuthPortalOrigin();
    if (portalOrigin && config.getMsalInteractionMode() === "popup") {
      this._ssoMessageHandler = (event) => {
        if (event.origin !== portalOrigin) return;
        const { type, idToken, message } = event.data || {};
        if (type === "SSO_AUTH_SUCCESS" && idToken) {
          if (typeof doMicrosoftLogin === "function") {
            doMicrosoftLogin({ idToken });
          }
          return;
        }
        if (type === "SSO_AUTH_ERROR") {
          this.setState({
            alertError: {
              title: intl.formatMessage({ id: "login.alert_title", defaultMessage: "Alert" }),
              message: message || intl.formatMessage({ id: "login.microsoft_sso_incomplete", defaultMessage: "Microsoft sign-in did not complete." }),
            },
          });
        }
      };
      window.addEventListener("message", this._ssoMessageHandler);
    }
  }

  componentWillUnmount() {
    if (this._ssoMessageHandler) {
      window.removeEventListener("message", this._ssoMessageHandler);
    }
  }

  getAuthPortalOrigin = () => {
    const raw = config.getAuthPortalUrl();
    if (!raw) return null;
    try {
      return new URL(raw).origin;
    } catch {
      return null;
    }
  }

  /**
   * After full-page SSO redirect, auth portal sends the user back with id_token or error in the hash.
   * Prefer router location; fall back to window.location.hash — router can lag one tick behind the browser URL.
   */
  _getSsoHash = (location) => {
    const fromRouter = (location && location.hash) || "";
    if (fromRouter && fromRouter.length > 1) return fromRouter;
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.length > 1) {
      return window.location.hash;
    }
    return "";
  };

  /**
   * After full-page SSO redirect, auth portal sends the user back with id_token or error in the hash.
   * Uses react-router `location` / `navigate` (via withRouter) instead of `window.location` / `history.replaceState`.
   */
  _consumeMicrosoftSsoHash = () => {
    const { location, navigate, doMicrosoftLogin, intl } = this.props;

    if (!location || typeof navigate !== "function") return;

    const { pathname, search } = location;
    const hash = this._getSsoHash(location);

    if (!hash || hash.length < 2) {
      this._lastMicrosoftSsoHash = null;
      return;
    }
    if (this._lastMicrosoftSsoHash === hash) return;
    this._lastMicrosoftSsoHash = hash;

    const frag = hash.startsWith("#") ? hash.slice(1) : hash;
    const params = new URLSearchParams(frag);
    const token = params.get("ms_sso_id_token");
    const err = params.get("ms_sso_error");

    if (token && typeof doMicrosoftLogin === "function") {
      doMicrosoftLogin({ idToken: token });
      // Drop the hash immediately so we do not re-read the same id_token on refresh / re-render (avoids loops and "stuck redirect" behaviour).
      const path = `${pathname || "/"}${search || ""}`;
      navigate({ pathname: pathname || "/", search: search || "", hash: "" }, { replace: true });
      if (typeof window !== "undefined" && window.history && window.location.hash) {
        window.history.replaceState(null, "", path);
      }
      return;
    }

    if (err) {
      let message = err;
      try {
        message = decodeURIComponent(err);
      } catch {
        /* keep raw */
      }
      this.setState({
        alertError: {
          title: intl.formatMessage({ id: "login.alert_title", defaultMessage: "Alert" }),
          message: message || intl.formatMessage({ id: "login.microsoft_sso_incomplete", defaultMessage: "Microsoft sign-in did not complete." }),
        },
      });
      const path = `${pathname || "/"}${search || ""}`;
      navigate({ pathname: pathname || "/", search: search || "", hash: "" }, { replace: true });
      if (typeof window !== "undefined" && window.history && window.location.hash) {
        window.history.replaceState(null, "", path);
      }
    }
  };

  _getRequestAccessUrl = () => {
    const sso = this.state.ssoRequestAccess;
    if (!sso) return "/request-access";
    const q = new URLSearchParams();
    if (sso.email) q.set("email", sso.email);
    if (sso.oid) q.set("oid", sso.oid);
    if (sso.tid) q.set("tid", sso.tid);
    if (sso.name) q.set("name", sso.name);
    if (sso.preferredUsername) q.set("preferredUsername", sso.preferredUsername);
    q.set("from", "sso");
    return `/request-access?${q.toString()}`;
  };

  _openMicrosoftLogin = (e) => {
    const { location, intl } = this.props;
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const portal = config.getAuthPortalUrl();
    if (!portal) {
      this.setState({
        alertError: {
          title: intl.formatMessage({ id: "login.alert_title", defaultMessage: "Alert" }),
          message: intl.formatMessage({ id: "login.microsoft_not_configured", defaultMessage: "Microsoft sign-in is not configured." }),
        },
      });
      return;
    }
    const base = String(portal).replace(/\/$/, "");
    const originParam = encodeURIComponent(getBrowserOrigin());
    const loc = location || { pathname: "/", search: "" };
    const returnPath = `${loc.pathname || "/"}${loc.search || ""}`;
    const returnParam = encodeURIComponent(returnPath);
    const mode = config.getMsalInteractionMode();
    if (mode === "popup") {
      window.open(`${base}?origin=${originParam}&interaction=popup`, "hermes-ms-sso", "popup=yes,width=520,height=640");
    } else {
      window.location.href = `${base}?origin=${originParam}&returnUrl=${returnParam}&interaction=redirect`;
    }
  };

  componentDidUpdate(prevProps, prevState) {
    const prevHash = this._getSsoHash(prevProps.location);
    const nextHash = this._getSsoHash(this.props.location);
    if (prevHash !== nextHash) {
      this._consumeMicrosoftSsoHash();
    }

    if (!_isEqual(prevProps.token, _get(this.props, "userToken", "")) && !_isEmpty(_get(this.props, "userToken", ""))) {
      const { cookies, userToken } = this.props;

      const tokenCookie = encryptUserData({ "userToken": userToken });
      cookies.set("userTokenData", tokenCookie, { path: "/", domain: `.${process.env.REACT_APP_FRONTEND_WEB_APP_DOMAIN}` });
    }

    if (!_isEqual(prevProps.loginError, _get(this.props, "loginError", {})) && !_isEmpty(_get(this.props, "loginError", {}))) {
      const sso = _get(this.props.loginError, "ssoRequestAccess");
      this.setState({
        alertError: sso && sso.email ? {} : {
          title: _get(this.props, "loginError.title", "Alert"),
          message: _get(this.props, "loginError.message", ""),
        },
        ssoRequestAccess: sso || null,
      });

      if (typeof this.props.clearAuthenticationError === "function") {
        this.props.clearAuthenticationError();
      }
    }

    if (!_isEqual(prevState.formData, _get(this.state, "formData", {}))) { this._handleValidation(); }
  }

  _handleValidation = () => {
    const { intl } = this.props;
    const { formData: { userName, password } } = this.state;
    let formErrors = {};

    if (!isEmailValid(userName)) {
      formErrors["userName"] = intl.formatMessage({ id: "error.invalid", defaultMessage: "Invalid {field}" }, {
        field: intl.formatMessage({ id: "email", defaultMessage: "email" }),
      });
    }

    if (_isEmpty(password)) {
      formErrors["password"] = intl.formatMessage({ id: "error.required", defaultMessage: "{field} is required" }, {
        field: intl.formatMessage({ id: "password", defaultMessage: "password" }),
      });
    }

    this.setState({
      alertError: {},
      ssoRequestAccess: null,
      disabled: !_isEmpty(formErrors),
      formErrors
    });
  }

  _doLogin = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const { doLogin, cookies } = this.props;
    const { formData: { userName, password, rememberMe }, formErrors } = this.state;

    if (_isEmpty(userName) || _isEmpty(password) || !_isEmpty(formErrors)) {
      this._handleValidation();

      return;
    }

    if (typeof doLogin === "function") { doLogin({ userName: userName, password: password }); }

    const saveCookieData = encryptUserData({ "userName": (userName || ""), "password": (password || ""), "rememberMe": (rememberMe || false) });

    if (rememberMe === true) {

      cookies.set("loginData", saveCookieData, { path: "/", domain: `.${process.env.REACT_APP_FRONTEND_WEB_APP_DOMAIN}` });
    } else {
      cookies.remove("loginData", { path: "/", domain: `.${process.env.REACT_APP_FRONTEND_WEB_APP_DOMAIN}` });
    }
  }

  _handleChange = (e) => {
    e.preventDefault();

    const { name, value } = (e.target || {});

    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value
      },
      alertError: {},
      ssoRequestAccess: null,
    }));
  }

  render() {
    const { userToken, location: { search = "" }, roleWiseRedirections, roleId, intl } = this.props;
    const { formData, formErrors, alertError, isLoginButtonClicked, ssoRequestAccess } = this.state;

    let redirectUrl = "/";

    if (!_isEmpty(userToken)) {
      const params = new URLSearchParams(search);

      (roleWiseRedirections || []).forEach((role) => {
        if ((role.roleIds || []).includes(roleId) && role.redirectionPath !== null) {
          redirectUrl = (role.redirectionPath || "/");
        }
      });

      const url = (params.get("continue") || redirectUrl);

      return (<Navigate to={url} />);
    }

    if (ssoRequestAccess && ssoRequestAccess.email) {
      return <Navigate to={this._getRequestAccessUrl()} replace />;
    }

    return (
      <>
        <Helmet>
          <title>
            {intl.formatMessage({ id: "hermes.login", defaultMessage: "Hermes" })}
          </title>
        </Helmet>

        <div className="login-wrapper">
          <div className="card">
            <div className="card-header">
              <img src={require("../../assets/images/logo.svg").default} className="img-logo img-fluid mx-auto d-block" alt="Hermes" />
            </div>

            <div className="card-body">
              {!_isEmpty(alertError) && (
                <div className="mb-3 text-danger text-start small" role="alert">
                  {alertError.title && !["Alert", "Forbidden"].includes(alertError.title) && (
                    <div className="fw-semibold mb-1">{alertError.title}</div>
                  )}
                  <span style={{ whiteSpace: "pre-line" }}>{(alertError.message || "")}</span>
                </div>
              )}

              <Form autoComplete="off" onSubmit={(e) => this._doLogin(e)}>
                <div className="mb-5">
                  <label className="form-label text-capitalize" htmlFor="useremail">
                    <FormattedMessage id="login.email" defaultMessage="email" />
                  </label>
                  <input id="useremail"
                    type="text"
                    name="userName"
                    className="form-control"
                    placeholder="Email"
                    value={_get(formData, "userName", "")}
                    onChange={(e) => this._handleChange(e)}
                  />
                  {(_get(formErrors, "userName", "")) && ((isLoginButtonClicked || false) === true) && (<ErrorMessage message={_get(formErrors, "userName", "")} />)}
                </div>

                <div className="mb-5">
                  <label className="form-label text-capitalize" htmlFor="userpassword">
                    <FormattedMessage id="login.password" defaultMessage="password" />
                  </label>
                  <input
                    id="userpassword"
                    type="password"
                    name="password"
                    className="form-control pe-14"
                    placeholder="Password"
                    value={_get(formData, "password", "")}
                    onChange={(e) => this._handleChange(e)}
                  />
                  {(_get(formErrors, "password", "")) && ((isLoginButtonClicked || false) === true) && (<ErrorMessage message={_get(formErrors, "password", "")} />)}
                </div>

                <div className="mb-7">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="rememberme"
                      className="form-check-input"
                      onClick={(e) =>
                        this.setState((prevState) => ({
                          formData: {
                            ...prevState.formData,
                            rememberMe: !_get(prevState, "formData.rememberMe", false)
                          },
                        }))
                      } />
                    <label className="form-check-label fw-medium ms-1 text-capitalize" htmlFor="rememberme">
                      <FormattedMessage id="login.remember_me" defaultMessage="remember me" />
                    </label>
                  </div>
                </div>

                <div className="mb-5">
                  <button type="submit" className="btn btn-primary waves-effect w-100 text-capitalize" onClick={() => this.setState({ isLoginButtonClicked: !isLoginButtonClicked })}>
                    <FormattedMessage id="login.login" defaultMessage="login" />
                  </button>
                </div>

                <div className="mb-19">
                  <button
                    type="button"
                    className="btn btn-microsoft waves-effect w-100 text-capitalize-first"
                    onClick={this._openMicrosoftLogin}
                  >
                    <img src={require("../../assets/images/microsoft-logo.svg").default} alt="Microsoft" />
                    <FormattedMessage id="login.microsoft" defaultMessage="Sign in with Microsoft" />
                  </button>
                </div>

                <div className="text-center">
                  <Link to="/forgot-password" className="link-grey-93 fw-medium text-capitalize-first">
                    <FormattedMessage id="login.forgot_password_link" defaultMessage="forgot your password?" />
                  </Link>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  userToken: _get(state, "authentication.userToken"),
  loginError: _get(state, "authentication.error", {}),
  roleId: _get(state, "authentication.userToken.role_id", null),
  roleWiseRedirections: _get(state, "application.constants.roleWiseRedirections", []),
});

const mapDispatchToProps = (dispatch) => ({
  clearAuthenticationError: () => dispatch(clearAuthenticationError()),
  doLogin: (data) => dispatch(doLogin(data)),
  doMicrosoftLogin: (data) => dispatch(doMicrosoftLogin(data)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(withCookies(LoginUser))));
